import { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import api, { setStepUpHandler, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, Lock, KeyRound, CreditCard, Loader2 } from "lucide-react";

const Ctx = createContext(null);
export const useStepUp = () => useContext(Ctx);

const METHODS = [
  ["password", "Şifre", Lock],
  ["pin", "PIN", KeyRound],
  ["card", "Kart", CreditCard],
];

// Kritik işlemi onaya göndermeden önce kullanıcıdan kimlik teyidi (şifre/PIN/kart) alır.
// 428 dönen her istek için lib/api interceptor'ı bu diyaloğu açar, token'ı alıp isteği tekrarlar.
export function StepUpProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("Bu işlem");
  const [method, setMethod] = useState("password");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const resolver = useRef(null);

  const request = useCallback((lbl) => new Promise((resolve, reject) => {
    setLabel(lbl || "Bu işlem"); setMethod("password"); setValue(""); setOpen(true);
    resolver.current = { resolve, reject };
  }), []);

  useEffect(() => { setStepUpHandler(request); }, [request]);

  const finish = (ok, token) => {
    setOpen(false); setValue(""); setBusy(false);
    const r = resolver.current; resolver.current = null;
    if (r) { ok ? r.resolve(token) : r.reject(new Error("step_up_cancelled")); }
  };

  const submit = async () => {
    if (!value.trim()) { toast.error("Doğrulama bilgisini girin"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/step-up", { method, value: value.trim(), action: label });
      toast.success("Kimlik doğrulandı");
      finish(true, data.step_up_token);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Kimlik doğrulama başarısız");
      setBusy(false);
    }
  };

  return (
    <Ctx.Provider value={{ request }}>
      {children}
      <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) finish(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="step-up-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><ShieldCheck className="h-5 w-5 text-brand-green" /> Kimlik Doğrulama</DialogTitle>
            <DialogDescription>
              <span className="font-semibold text-slate-700">{label}</span> işlemini onaya göndermeden önce, işlemi <b>sizin</b> yaptığınızdan emin olmak için kimliğinizi doğrulayın. Başkasının açık oturumundan işlem yapılamaz.
            </DialogDescription>
          </DialogHeader>
          <div className="mb-3 flex gap-2">
            {METHODS.map(([k, l, Icon]) => (
              <button key={k} type="button" data-testid={`step-up-method-${k}`} onClick={() => { setMethod(k); setValue(""); }}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${method === k ? "border-brand-green bg-brand-green/10 text-brand-green" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                <Icon className="h-4 w-4" /> {l}
              </button>
            ))}
          </div>
          <Input
            data-testid="step-up-input"
            type={method === "card" ? "text" : "password"}
            inputMode={method === "pin" ? "numeric" : undefined}
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !busy) submit(); }}
            placeholder={method === "password" ? "Hesap şifreniz" : method === "pin" ? "POS PIN" : "Kart kodu"}
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="ghost" data-testid="step-up-cancel" onClick={() => finish(false)} disabled={busy}>Vazgeç</Button>
            <Button type="button" data-testid="step-up-submit" onClick={submit} disabled={busy} className="bg-brand-green text-white hover:bg-brand-green-600">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />} Doğrula & Gönder
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}
